import { useCallback, useState } from 'react';

import { isModelId, type ModelId } from '../../services/modelCatalog';

const LOOP_MODEL_KEY = 'loop_model';

/**
 * New loops start on Dopamine Ultra 1 rather than the shared composer default (GPT 6 Astra): a browser run takes
 * many model steps, and on the pricier model a small balance could not cover even the first one.
 */
export const LOOP_DEFAULT_MODEL: ModelId = 'dopamine-ultra';

/** The model last picked in the Loops composer, kept apart from the preference chat and planning share. */
export const readLoopModel = (): ModelId => {
  try {
    const stored = localStorage.getItem(LOOP_MODEL_KEY);

    return isModelId(stored) ? stored : LOOP_DEFAULT_MODEL;
  } catch {
    return LOOP_DEFAULT_MODEL;
  }
};

export const saveLoopModel = (modelId: ModelId): void => {
  try {
    localStorage.setItem(LOOP_MODEL_KEY, modelId);
  } catch {
    /* storage unavailable: the choice just lives for this page */
  }
};

/** The new-loop model: a landing hand-off wins, then the last Loops pick, then the cheap default. */
export const useLoopModelChoice = (handedOff?: ModelId): readonly [ModelId, (next: ModelId) => void] => {
  const [modelId, setModelId] = useState<ModelId>(() => handedOff || readLoopModel());
  const pickModel = useCallback((next: ModelId) => {
    saveLoopModel(next);
    setModelId(next);
  }, []);

  return [modelId, pickModel] as const;
};
